<template>
  <form :class="{ 'form': true, 'is-hidden': isHidden }">
    <svg class="logo">
      <use xlink:href="#bookero-logo" />
    </svg>

    <message
      v-if="passwordHasBeenChanged"
      type="success">
      {{ $t('login.passwordChanged') }}
    </message>

    <message
      v-if="autoLoginWrongAccessData"
      type="error">
      {{ $t('login.invalidData') }}
    </message>

    <message
      v-if="autoLoginError"
      type="error">
      {{ $t('login.anErrorOccurredUseFormBelow') }}
    </message>

    <div class="wrapper">
      <h1>{{ $t('login.title') }}</h1>

      <hr>

      <transition-group name="slide-down">
        <message
          v-if="emailIsEmpty || passwordIsEmpty"
          type="error"
          key="msg-1">
          {{ $t('login.allFieldsAreRequired') }}
        </message>

        <message
          v-if="emailIsInvalid || passwordIsInvalid"
          type="error"
          key="msg-2">
          {{ $t('login.invalidLoginData') }}
        </message>

        <message
          v-if="connectionError"
          type="error"
          key="msg-3">
          {{ $t('login.anErrorOccurredPleaseCheckInternetConnection') }}
        </message>
      </transition-group>

      <label>
        {{ $t('login.email') }}
        <input
          type="email"
          v-model="email"
          @keyup="removeEmpty('email')"
          @focus="removeError('email')"
          tabindex="1"
          :class="{ 'is-invalid': emailIsInvalid || emailIsEmpty }"
          :placeholder="$t('login.yourEmail')" />
      </label>

      <label>
        {{ $t('login.password') }}

        <router-link
          to="/password/reminder"
          tabindex="5">
          {{ $t('login.passwordReminder') }}
        </router-link>

        <input
          type="password"
          v-model="password"
          @keyup="removeEmpty('password')"
          @focus="removeError('password')"
          tabindex="2"
          :class="{ 'is-invalid': passwordIsInvalid || passwordIsEmpty }"
          spellcheck="false"
          :placeholder="$t('login.yourPassword')" />
      </label>

      <btn
        type="success wide"
        @click.native="logIn"
        :disabled="formSent"
        :tabindex="3">
        <template v-if="!formSent">{{ $t('login.login') }}</template>
        <template v-if="formSent">{{ $t('login.logging') }}</template>
      </btn>

      <div
        v-if="$config.registrationEnabled"
        class="footer">
        <span>{{ $t('login.noAccount') }}</span>
        <router-link
          to="/register"
          tabindex="4">
          {{ $t('login.registerForFree') }}
        </router-link>
      </div>
    </div>
  </form>
</template>

<script>
import Vue from 'vue';
import SanitizeUtils from './../helpers/SanitizeUtils';

export default {
  name: 'login',
  data () {
    return {
      isHidden: false,
      email: '',
      password: '',
      formSent: false,
      emailIsInvalid: false,
      passwordIsInvalid: false,
      emailIsEmpty: false,
      passwordIsEmpty: false,
      connectionError: false,
      passwordHasBeenChanged: false,
      autoLoginWrongAccessData: false,
      autoLoginError: false
    };
  },
  mounted () {
    localStorage.removeItem('bookero-auth-token');
    Vue.prototype.$http.defaults.headers['Authorization'] = 'Bearer null';
    this.$store.commit('config/clearConfig');
    this.$store.commit('global/clear');
    this.$bus.$emit('logout');

    if (typeof window.bugsnagClient !== 'undefined') {
      window.bugsnagClient.user = { id: 0 };
    }

    if (typeof window.ga !== 'undefined') {
      window.ga('set', 'dimension1', 0);
    }

    if (this.$route.params.case === 'after-password-reset') {
      this.passwordHasBeenChanged = true;
    }

    if (this.$route.params.case === 'auto-login-wrong-data') {
      this.autoLoginWrongAccessData = true;
    }

    if (this.$route.params.case === 'auto-login-error') {
      this.autoLoginError = true;
    }
  },
  methods: {
    logIn (e) {
      e.preventDefault();
      this.connectionError = false;

      if (!this.validate()) {
        return;
      }

      this.formSent = true;

      this.$http.post('/login', {
        'email': this.email,
        'password': this.password
      }).then(response => {
        localStorage.setItem('bookero-auth-token', response.data.token);
        localStorage.setItem('bookero-last-visit-time', new Date().getTime());
        Vue.prototype.$http.defaults.headers['Authorization'] = 'Bearer ' + SanitizeUtils.getToken();
        this.formSent = false;
        this.isHidden = true;

        setTimeout(() => {
          this.$router.push('/dashboard');

          setTimeout(() => {
            this.$bus.$emit('login-success');

            if (window.dataLayer) {
              window.dataLayer.push({ 'eventForm': 'login' });
            }
          }, 1000);
        }, 750);
      }).catch(error => {
        if (error.response && error.response.status === 401) {
          this.emailIsInvalid = true;
          this.passwordIsInvalid = true;
        }

        if (!error.response) {
          this.connectionError = true;

          setTimeout(() => {
            this.connectionError = false;
          }, 3000);
        }

        this.formSent = false;
      });
    },
    validate () {
      let formIsValid = true;

      if (this.email.trim() === '') {
        this.emailIsEmpty = true;
        formIsValid = false;
      }

      if (this.password.trim() === '') {
        this.passwordIsEmpty = true;
        formIsValid = false;
      }

      return formIsValid;
    },
    removeError (field) {
      if (field === 'email') {
        this.emailIsInvalid = false;
      }

      if (field === 'password') {
        this.passwordIsInvalid = false;
      }
    },
    removeEmpty (field) {
      if (field === 'email') {
        this.emailIsEmpty = false;
      }

      if (field === 'password') {
        this.passwordIsEmpty = false;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.form {
  left: 50%;
  margin: 0;
  max-width: 94%;
  padding: 3.2rem 0;
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  transition: opacity .5s ease-out;
  width: 41rem;
  will-change: opacity;

  &.is-hidden {
    opacity: 0;
    pointer-events: none;
  }

  .logo {
    display: block;
    height: 3.6rem;
    margin: 0 auto 3.2rem auto;
  }

  .wrapper {
    background: $color-bg-lightest;
    border-radius: $border-radius-small;
    padding: 3rem;

    h1 {
      font-size: 2.4rem;
      font-weight: 400;
      margin: 0 0 3rem 0;
      text-align: center;
    }

    hr {
      border: none;
      border-top: 1px solid $color-border;
      margin-bottom: 3rem;
    }

    label {
      display: block;
      font-weight: 400;
      position: relative;

      input {
        background: $color-bg-lightest;
        border: 1px solid $color-border;
        border-radius: $border-radius-small;
        font-size: 1.4rem;
        margin: .5rem 0 1rem 0;
        padding: 1rem;
        width: 100%;

        &::-webkit-input-placeholder {
          color: $color-text-lightest;
        }

        &::-moz-placeholder {
          color: $color-text-lightest;
        }

        &:-ms-input-placeholder {
          color: $color-text-lightest;
        }

        &:-moz-placeholder {
          color: $color-text-lightest;
        }

        &.is-invalid {
          border-color: $color-danger;
          color: $color-danger;
        }
      }

      a {
        font-size: 1.4rem;
        position: absolute;
        right: 0;
        top: .25rem;
      }
    }

    & > .button {
      margin: 1.5rem auto;
    }
  }

  .footer {
    background: $color-bg-medium;
    border-radius: 0 0 $border-radius-small $border-radius-small;
    margin: 3rem -3rem -3rem -3rem;
    padding: 2rem 3rem;
    text-align: center;

    a,
    span {
      display: block;
      line-height: 1.5;
    }
  }
}

.slide-down-enter-active, .slide-down-leave-active {
  transition: all .25s ease-out;
}

.slide-down-enter, .slide-down-leave-to {
  height: 0;
  opacity: 0;
}

@media (max-width: 480px) {
  .form {
    .wrapper {
      padding: 2rem;
    }

    .footer {
      margin: 3rem -2rem -2rem -2rem;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/views/Login.vue